import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from '@client/shared/utils/routerUtils/routerUtils';
import { ERROR, AN_ERROR_OCCURRED } from '@client/definitions/constants';
import styleHelper from '@client/shared/styles/styleHelper.scss';
import { NotificationContext } from '@client/components/notificationProvider/NotificationProvider';
import pdfLogo90 from '@client/shared/images/pdf_logo_90.png';
import pdfLogo from '@client/shared/images/pdf_logo.png';
import pdfHeart from '@client/shared/images/pdf_heart.png';
import fontIntroHL from '@client/shared/fonts/Intro-hl/Intro-hl-bold/Intro-hl-bold.ttf';
import { downloadFile, roundedRectData } from '../../shared/utils/pdf/pdfUtils';
import { getSwishQRCode } from '../../connectivity/api.frontend';
import styles from './qrButton.scss';

const PADDING = 30;
const OFFSET = 50;

const QRButton = ({ guidCode }) => {
  const context = useContext(NotificationContext);

  const loadImage = async (pdfDoc, imgUrl) => {
    const arrayBuffer = await fetch(imgUrl).then((r) => r.arrayBuffer());
    return pdfDoc.embedPng(arrayBuffer);
  };

  const generatePDF = async (pngData) => {
    const { PDFDocument, rgb } = await import('pdf-lib');
    const { default: fontkit } = await import('@pdf-lib/fontkit');
    const pdfDoc = await PDFDocument.create();
    pdfDoc.registerFontkit(fontkit);

    // Load custom font
    const fontBytes = await fetch(fontIntroHL).then((res) => res.arrayBuffer());
    const introHlFont = await pdfDoc.embedFont(fontBytes);

    // Embed images
    const imgQRCode = await pdfDoc.embedPng(pngData);
    const imgLogo = await loadImage(pdfDoc, pdfLogo);
    const img90Account = await loadImage(pdfDoc, pdfLogo90);
    const imgHeart = await loadImage(pdfDoc, pdfHeart);

    // Scale images
    const qrRect = imgQRCode.scale(0.5);
    const rectLogo = imgLogo.scale(0.25);
    const rectLogo90 = img90Account.scale(0.07);
    const rectHeart = imgHeart.scale(0.4);

    // Add a blank page to the document
    const page = pdfDoc.addPage();
    const { width, height } = page.getSize();

    // Draw text
    page.setFont(introHlFont);
    page.drawText('STÖD MIN', {
      x: 155,
      y: height * 0.5 + 100 - OFFSET,
      size: 56,
    });

    page.drawText('INSAMLING', {
      x: 130,
      y: height * 0.5 + 40 - OFFSET,
      size: 56,
    });

    page.drawText('DIN GÅVA KAN RÄDDA LIV', {
      x: 140,
      y: height * 0.5 - 285 - OFFSET,
      size: 24,
    });

    // Draw QR frame
    const path = roundedRectData(230, 230, 16);
    page.drawSvgPath(path, {
      x: width * 0.5 - 115,
      y: height * 0.5 - OFFSET,
      color: rgb(0.95, 0.95, 0.95),
    });

    // Draw QR code
    page.drawImage(imgQRCode, {
      x: width * 0.5 - qrRect.width * 0.5,
      y: height * 0.5 - qrRect.height * 0.5 - 115 - OFFSET,
      width: qrRect.width,
      height: qrRect.height,
    });

    // Draw large heart
    page.drawImage(imgHeart, {
      x: width * 0.5,
      y: height * 0.5 + 160 - OFFSET,
      width: rectHeart.width,
      height: rectHeart.height,
    });

    // Draw Logos
    page.drawImage(imgLogo, {
      x: PADDING,
      y: height - PADDING - rectLogo.height,
      width: rectLogo.width,
      height: rectLogo.height,
    });

    page.drawImage(img90Account, {
      x: width - PADDING - rectLogo90.width,
      y: height - PADDING - rectLogo90.height,
      width: rectLogo90.width,
      height: rectLogo90.height,
    });

    // Serialize the PDFDocument
    const pdfBytes = await pdfDoc.save();
    const bytes = new Uint8Array(pdfBytes);
    const blob = new Blob([bytes], { type: 'application/pdf' });

    downloadFile(blob, `ei_swish_qr_${Date.now()}.pdf`);
  };

  const downloadSwishQRCode = () => {
    getSwishQRCode(guidCode)
      .then((response) => {
        if (response.status === 200 && response.data) {
          generatePDF(response.data);
        } else {
          context.displayNotification(ERROR, AN_ERROR_OCCURRED, 5000);
        }
      })
      .catch(() => {
        context.displayNotification(ERROR, AN_ERROR_OCCURRED, 5000);
      });
  };

  return (
    <div className={styles.wrapper}>
      <button
        className={`${styleHelper.btnPrimaryMd}`}
        onClick={downloadSwishQRCode}
      >
        Generera QR-kod
      </button>
    </div>
  );
};

QRButton.propTypes = {
  guidCode: PropTypes.string,
};

export default withRouter(QRButton);
